/**
 * SubstitutionModal — Smart Exercise Swap Sheet
 *
 * Filters the exercise pool by:
 *   1. Same primary muscle group as the exercise being replaced
 *   2. Available equipment (from active gym profile)
 *   3. Same split day
 *
 * Sorted by: usage frequency (most-used first) → alphabetical
 */

import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { Exercise, MuscleGroup } from '@/types';

interface SubstitutionModalProps {
  targetMuscle: MuscleGroup;
  currentExerciseId: string;
  availableEquipment: string[];
  usageCount: Map<string, number>;
  onSelect: (exercise: Exercise) => void;
  onClose: () => void;
}

const EQUIPMENT_ICONS: Record<string, string> = {
  barbell: '🏋️',
  dumbbell: '💪',
  cable: '🔗',
  machine: '⚙️',
  kettlebell: '🔔',
  bodyweight: '🤸',
  bands: '🎗️',
  ez_bar: '〰️',
  trap_bar: '⬡',
};

const MUSCLE_LABELS: Partial<Record<MuscleGroup, string>> = {
  chest: 'Chest', front_delt: 'Front Delt', lateral_delt: 'Lateral Delt',
  rear_delt: 'Rear Delt', triceps: 'Triceps', biceps: 'Biceps',
  lats: 'Lats', rhomboids: 'Rhomboids', traps: 'Traps',
  quads: 'Quads', hamstrings: 'Hamstrings', glutes: 'Glutes',
  calves: 'Calves', core: 'Core', lower_back: 'Lower Back',
  forearms: 'Forearms', hip_flexors: 'Hip Flexors',
};

export default function SubstitutionModal({
  targetMuscle,
  currentExerciseId,
  availableEquipment,
  usageCount,
  onSelect,
  onClose,
}: SubstitutionModalProps) {
  // Live query so newly added custom exercises appear instantly
  const allExercises = useLiveQuery(() => db.exercises.toArray(), []);

  const candidates = useMemo(() => {
    if (!allExercises) return [];

    return allExercises
      .filter((ex) => {
        // Not the current exercise
        if (ex.id === currentExerciseId) return false;
        // Primary muscle matches
        if (ex.muscles[0]?.muscle !== targetMuscle) return false;
        // Equipment available
        if (!ex.equipment.some((eq) => availableEquipment.includes(eq))) return false;
        return true;
      })
      .sort((a, b) => {
        // Sort by usage count descending, then alphabetical
        const ua = usageCount.get(a.id) ?? 0;
        const ub = usageCount.get(b.id) ?? 0;
        if (ub !== ua) return ub - ua;
        return a.name.localeCompare(b.name);
      });
  }, [allExercises, targetMuscle, currentExerciseId, availableEquipment, usageCount]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close substitution modal"
      />

      {/* Sheet */}
      <div className="relative w-full max-w-lg bg-surface-100 rounded-t-3xl
                      pt-4 pb-4 shadow-card-lg animate-slide-up
                      max-h-[75vh] flex flex-col">

        {/* Handle */}
        <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-4 flex-shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 mb-3 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-ink">Swap Exercise</h3>
            <p className="text-xs text-ink-muted mt-0.5">
              {MUSCLE_LABELS[targetMuscle] ?? targetMuscle} · {candidates.length} alternatives
            </p>
          </div>
          <button className="btn-ghost p-1 text-lg" onClick={onClose}>✕</button>
        </div>

        {/* Scrollable list */}
        <div className="overflow-y-auto flex-1 px-4 space-y-2 no-scrollbar">
          {candidates.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-ink-muted text-sm">No alternatives available</p>
              <p className="text-ink-faint text-xs mt-1">Try enabling more equipment in Settings</p>
            </div>
          ) : (
            candidates.map((ex) => {
              const uses = usageCount.get(ex.id) ?? 0;
              const equipIcons = ex.equipment
                .slice(0, 2)
                .map((eq) => EQUIPMENT_ICONS[eq] ?? '•')
                .join(' ');

              return (
                <button
                  key={ex.id}
                  className="w-full card flex items-center gap-4 text-left
                             hover:bg-surface-200 active:scale-[0.98]
                             transition-all duration-150"
                  onClick={() => { onSelect(ex); onClose(); }}
                >
                  {/* Category pill */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center
                                   justify-center text-xs font-bold
                                   ${ex.category === 'compound'
                                     ? 'bg-accent/20 text-accent'
                                     : 'bg-surface-400 text-ink-muted'}`}>
                    {ex.category === 'compound' ? 'C' : 'I'}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink text-sm truncate">{ex.name}</p>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {equipIcons} · {ex.category}
                    </p>
                  </div>

                  {uses > 0 && (
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-ink-faint">used</p>
                      <p className="text-sm font-bold text-ink-muted">{uses}×</p>
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
